import { fabric } from 'fabric';
import type { ImagePlaceholderElement as ImagePlaceholder } from '@/typings/customilyElements/canvasElements';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isFeatureEnabled = (_featureName: string): boolean => {
    return false;
};

const CHIN_INDEX = 8;
const MIN_LANDMARKS_LENGTH = 68;
const DEBUG_CANVAS_ID = 'face-adjustment-debug-canvas';

const computeContainScale = (containerW: number, containerH: number, sourceW: number, sourceH: number) => {
    if (!containerW || !containerH || !sourceW || !sourceH) return 1;
    return Math.min(containerW / sourceW, containerH / sourceH);
};

type FaceAnalysisMetadata = {
    faceAnalysis?: {
        faces?: number[][];
        points?: number[][][];
    };
};

type FaceMetrics = {
    centerX: number;
    centerY: number;
    bottomY: number;
    width: number;
    height: number;
    area: number;
};

type LandmarkPoint = {
    x: number;
    y: number;
};

const FACE_OUTLINE_INDICES = Array.from({ length: 17 }, (_, index) => index);
const EYEBROW_INDICES = Array.from({ length: 10 }, (_, index) => index + 17);

const asNumber = (value: unknown): number => Number(value);

const isFinitePointLike = (point: unknown): point is number[] => {
    if (!Array.isArray(point) || point.length < 2) return false;
    const x = asNumber(point[0]);
    const y = asNumber(point[1]);
    return Number.isFinite(x) && Number.isFinite(y);
};

// The module only supports a single detected face to avoid ambiguous alignment.
const getSingleFaceLandmarks = (metadata?: FaceAnalysisMetadata): number[][] | null => {
    const points = metadata?.faceAnalysis?.points;
    if (!Array.isArray(points) || points.length !== 1) return null;

    const landmarks = points[0];
    if (!Array.isArray(landmarks) || landmarks.length < MIN_LANDMARKS_LENGTH) return null;

    return landmarks;
};

// Maps a landmark index list to validated XY points.
const getIndexedPoints = (landmarks: number[][], indices: number[]): LandmarkPoint[] | null => {
    const mapped = indices.map((index) => landmarks[index]);
    if (mapped.some((point) => !isFinitePointLike(point))) return null;

    return mapped.map((point) => ({
        x: asNumber(point[0]),
        y: asNumber(point[1])
    }));
};

const getMinMax = (values: number[]) => ({
    min: Math.min(...values),
    max: Math.max(...values)
});

/**
 * Builds a stable face metric using internal landmarks.
 * Height is measured from eyebrow top to chin to reduce hair/hat influence.
 */
export const extractFaceMetrics = (metadata?: FaceAnalysisMetadata): FaceMetrics | null => {
    const landmarks = getSingleFaceLandmarks(metadata);
    if (!landmarks) return null;

    const faceOutlinePoints = getIndexedPoints(landmarks, FACE_OUTLINE_INDICES);
    const eyebrowPoints = getIndexedPoints(landmarks, EYEBROW_INDICES);
    const chin = landmarks[CHIN_INDEX];

    if (!faceOutlinePoints || !eyebrowPoints || !isFinitePointLike(chin)) {
        return null;
    }

    const xCoords = faceOutlinePoints.map((point) => point.x);
    const browTopY = Math.min(...eyebrowPoints.map((point) => point.y));
    const chinY = asNumber(chin[1]);

    if (!Number.isFinite(browTopY) || !Number.isFinite(chinY)) return null;

    const { min: minX, max: maxX } = getMinMax(xCoords);
    const width = maxX - minX;
    const height = chinY - browTopY;

    if (width <= 0 || height <= 0) {
        return null;
    }

    return {
        centerX: (minX + maxX) / 2,
        centerY: (browTopY + chinY) / 2,
        bottomY: chinY,
        width,
        height,
        area: width * height
    };
};

/**
 * Paints debug landmarks from metadata on a 2D context.
 */
const drawDebugLandmarks = (
    ctx: CanvasRenderingContext2D | null,
    metadata?: FaceAnalysisMetadata,
    options?: {
        color?: string;
        drawLabels?: boolean;
    }
): void => {
    if (!ctx) {
        return;
    }

    const landmarks = getSingleFaceLandmarks(metadata);
    if (!landmarks) {
        return;
    }

    ctx.save();
    const color = options?.color || '#FF0000';
    const drawLabels = options?.drawLabels ?? false;

    ctx.strokeStyle = color;
    ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
    ctx.lineWidth = 2;

    // Draw all 68 landmarks as small circles
    landmarks.forEach((point, index) => {
        if (!isFinitePointLike(point)) {
            return;
        }

        const x = asNumber(point[0]);
        const y = asNumber(point[1]);
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        if (drawLabels && index === CHIN_INDEX) {
            ctx.fillStyle = 'rgb(0, 255, 0)';
            ctx.font = '12px Arial';
            ctx.fillText('Chin', x + 5, y);
            ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
        }
    });

    ctx.restore();
};

/**
 * Paints the face box points (8 corners) with connecting lines from metadata.
 */
const drawFaceBoxPoints = (ctx: CanvasRenderingContext2D, faces?: number[][]): void => {
    if (!faces || faces.length === 0) return;

    const facePoints = faces[0];
    if (!Array.isArray(facePoints) || facePoints.length < 8) return;

    const points: [number, number][] = [];
    for (let i = 0; i < 8; i += 2) {
        const x = asNumber(facePoints[i]);
        const y = asNumber(facePoints[i + 1]);
        if (Number.isFinite(x) && Number.isFinite(y)) {
            points.push([x, y]);
        }
    }

    if (points.length === 0) return;

    ctx.save();
    ctx.strokeStyle = '#DD33FF';
    ctx.fillStyle = 'rgba(221, 51, 255, 0.3)';
    ctx.lineWidth = 2;

    // Draw points
    points.forEach(([x, y]) => {
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    });

    // Draw connecting lines
    ctx.strokeStyle = '#DD33FF';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i][0], points[i][1]);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
};

const getOrCreateDebugCanvas = (width: number, height: number): HTMLCanvasElement | null => {
    if (typeof document === 'undefined') return null;

    let debugCanvas = document.getElementById(DEBUG_CANVAS_ID) as HTMLCanvasElement | null;

    if (!debugCanvas) {
        debugCanvas = document.createElement('canvas');
        debugCanvas.id = DEBUG_CANVAS_ID;
        debugCanvas.style.position = 'fixed';
        debugCanvas.style.top = '8px';
        debugCanvas.style.right = '8px';
        debugCanvas.style.zIndex = '99999';
        debugCanvas.style.background = 'rgba(0, 0, 0, 0.65)';
        debugCanvas.style.border = '1px solid #ffffff';
        debugCanvas.style.maxWidth = '30vw';
        debugCanvas.style.maxHeight = '40vh';
        debugCanvas.style.pointerEvents = 'none';
        document.body.appendChild(debugCanvas);
    }

    if (debugCanvas.width !== width) debugCanvas.width = width;
    if (debugCanvas.height !== height) debugCanvas.height = height;

    return debugCanvas;
};

// Normalizes element dimensions from natural size when available.
const getImageDimensions = (object: ImagePlaceholder): { width: number; height: number } | null => {
    const imgElement = object.originalElement;
    const width = imgElement?.naturalWidth || imgElement?.width;
    const height = imgElement?.naturalHeight || imgElement?.height;
    if (!width || !height) return null;
    return { width, height };
};

// Draws the original image plus extracted landmarks in the floating debug overlay.
const drawDebugOverlay = (
    imgElement: HTMLImageElement,
    metadata: FaceAnalysisMetadata,
    faceMetrics: FaceMetrics,
    width: number,
    height: number
): void => {
    const canvas = getOrCreateDebugCanvas(width, height);
    const ctx = canvas?.getContext('2d') || null;
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(imgElement, 0, 0, width, height);
    drawDebugLandmarks(ctx, metadata);
    drawFaceBoxPoints(ctx, metadata?.faceAnalysis?.faces);
};

type InitBoxValues = {
    height: number;
    centerX: number;
    centerY: number;
    rotation: number;
};

type Vector = {
    x: number;
    y: number;
};

// Reads the placeholder's "intent" box: where and how big the photo was meant to be,
// and at what angle, when the designer set up the placeholder.
const getInitBoxValues = (object: ImagePlaceholder, boundsRect: { height?: number }): InitBoxValues => ({
    height: object.initHeight || boundsRect.height || 0,
    centerX: object.initCenterX || 0,
    centerY: object.initCenterY || 0,
    rotation: object.initRotation || 0
});

// Scale needed so the detected face's height matches the intended placeholder face height.
const computeRelativeScale = (
    initHeight: number,
    faceHeight: number,
    boundsRect: { width?: number; height?: number },
    imgW: number,
    imgH: number
): { targetScale: number; relativeScale: number } => {
    const targetScale = initHeight / faceHeight;
    const baseScale = computeContainScale(boundsRect.width || 0, boundsRect.height || 0, imgW, imgH);
    const relativeScale = baseScale > 0 ? targetScale / baseScale : 1;
    return { targetScale, relativeScale };
};

// Bottom-center anchor of the init box, in the placeholder's local frame. The box is
// itself rotated around its center, so "bottom edge" isn't simply
// (centerX, centerY + height / 2) - the (0, height / 2) offset from the box's center has
// to be rotated along with the box.
const computeRotatedBoxTarget = (initBox: InitBoxValues, rotationRadians: number): Vector => {
    const bottomOffset = fabric.util.rotateVector({ x: 0, y: initBox.height / 2 }, rotationRadians);
    return { x: initBox.centerX + bottomOffset.x, y: initBox.centerY + bottomOffset.y };
};

// The face anchor (center-x, chin-y) is measured on the raw, unrotated image, but the
// image itself is rendered rotated by initRotation inside the mask (offset is the
// rotation pivot, applied before the rotation - see fabricMaskedImage._render). Rotate
// the face vector the same way so it describes the same (rotated) frame as the target.
const computeRotatedFaceVector = (
    faceMetrics: FaceMetrics,
    imgW: number,
    imgH: number,
    rotationRadians: number
): Vector =>
    fabric.util.rotateVector({ x: faceMetrics.centerX - imgW / 2, y: faceMetrics.bottomY - imgH / 2 }, rotationRadians);

// Combines the target box position and the face vector into the offset
// setEditModeImageOffset expects: already rotated by the placeholder's own angle (same
// convention as fabricImagePlaceholder.setOffSetsBasedOnInitValues).
const computeFinalOffset = (
    target: Vector,
    faceVector: Vector,
    targetScale: number,
    placeholderAngle: number
): Vector => {
    const localOffset = {
        x: target.x / targetScale - faceVector.x,
        y: target.y / targetScale - faceVector.y
    };
    return fabric.util.rotateVector(localOffset, fabric.util.degreesToRadians(placeholderAngle));
};

/**
 * Dynamically adjusts the image by relating the detected face points
 * with the placeholder's initialization values (intent).
 */
export const applyFaceAdjustment = (object: ImagePlaceholder, parsedMetadata?: Record<string, unknown>): void => {
    const metadata = parsedMetadata as FaceAnalysisMetadata;
    const faceMetrics = extractFaceMetrics(metadata);
    if (!faceMetrics) return;

    const imageDimensions = getImageDimensions(object);
    if (!imageDimensions) return;
    const { width: imgW, height: imgH } = imageDimensions;

    const imgElement = object.originalElement;
    if (!imgElement) return;

    const boundsRect = object.item(1);
    if (!boundsRect) return;

    const previousEditMode = object._editMode;
    object._editMode = true;

    try {
        const initBox = getInitBoxValues(object, boundsRect);
        const { targetScale, relativeScale } = computeRelativeScale(
            initBox.height,
            faceMetrics.height,
            boundsRect,
            imgW,
            imgH
        );

        object.setEditModeImageScale(relativeScale);

        // setMask() resets the image's internal rotation to 0 and only restores it from
        // initRotation when hasInitValues() passes (it also requires initWidth, which isn't
        // always populated). Set it explicitly so the render always matches the rotation
        // assumed by the offset math below, regardless of that internal state.
        object.setEditModeImageRotation?.(initBox.rotation);

        const rotationRadians = fabric.util.degreesToRadians(initBox.rotation);
        const target = computeRotatedBoxTarget(initBox, rotationRadians);
        const faceVector = computeRotatedFaceVector(faceMetrics, imgW, imgH, rotationRadians);
        const { x: offsetX, y: offsetY } = computeFinalOffset(target, faceVector, targetScale, object.angle || 0);

        if (Number.isFinite(offsetX) && Number.isFinite(offsetY)) {
            object.setEditModeImageOffset(offsetX, offsetY);
        }
        object._updateRelationshipMatrix();

        // Debug visualization
        if (isFeatureEnabled('DEBUG_FACE_ADJUSTMENT')) {
            drawDebugOverlay(imgElement, metadata, faceMetrics, imgW, imgH);
        }
    } catch (error) {
        console.error('Error applying face adjustment:', error);
    } finally {
        object._editMode = previousEditMode;
    }
};
